import { create } from "zustand";
import {
  catacombsApi,
  type CatacombsShop,
  type CatacombsState,
} from "../api/catacombs";
import { useBattleStore } from "./useBattleStore";
import { useGameStore } from "./useGameStore";

type CatacombsTab = "fight" | "auto" | "shop";

interface CatacombsStore {
  state: CatacombsState | null;
  shop: CatacombsShop | null;
  tab: CatacombsTab;
  loading: boolean;
  fighting: boolean;
  actionLoading: boolean;
  error: string | null;

  setTab: (tab: CatacombsTab) => void;
  refresh: () => Promise<void>;
  refreshShop: () => Promise<void>;
  fight: () => Promise<void>;
  autoClearStart: () => Promise<void>;
  autoClearClaim: () => Promise<void>;
  autoClearSkip: () => Promise<void>;
  buyItem: (code: string) => Promise<void>;
}

export const useCatacombsStore = create<CatacombsStore>((set, get) => ({
  state: null,
  shop: null,
  tab: "fight",
  loading: false,
  fighting: false,
  actionLoading: false,
  error: null,

  setTab: (tab) => {
    set({ tab, error: null });
    if (tab === "shop") void get().refreshShop();
  },

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const state = await catacombsApi.getState();
      set({ state });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load catacombs" });
    } finally {
      set({ loading: false });
    }
  },

  refreshShop: async () => {
    try {
      const shop = await catacombsApi.getShop();
      set({ shop });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load shop" });
    }
  },

  fight: async () => {
    set({ fighting: true, error: null });
    try {
      const res = await catacombsApi.fight();
      await useBattleStore.getState().loadBattle(res.battle_id, res.info_messages);
      useGameStore.getState().setScreen("battle");
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Fight failed" });
    } finally {
      set({ fighting: false });
    }
  },

  autoClearStart: async () => {
    set({ actionLoading: true, error: null });
    try {
      const res = await catacombsApi.autoClearStart();
      set({ state: res.state });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to start auto-clear" });
    } finally {
      set({ actionLoading: false });
    }
  },

  autoClearClaim: async () => {
    set({ actionLoading: true, error: null });
    try {
      const res = await catacombsApi.autoClearClaim();
      set({ state: res.state });
      useGameStore.getState().pushInfoMessages(res.info_messages ?? []);
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to claim rewards" });
    } finally {
      set({ actionLoading: false });
    }
  },

  autoClearSkip: async () => {
    set({ actionLoading: true, error: null });
    try {
      const res = await catacombsApi.autoClearSkip();
      set({ state: res.state });
      useGameStore.getState().pushInfoMessages(res.info_messages ?? []);
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to skip timer" });
    } finally {
      set({ actionLoading: false });
    }
  },

  buyItem: async (code: string) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await catacombsApi.buyShopItem(code);
      set({ shop: res.shop, state: get().state ? { ...get().state!, crypto: res.shop.crypto } : null });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Purchase failed" });
    } finally {
      set({ actionLoading: false });
    }
  },
}));
